/**
 * WebSocket Hook for Real-time Messages
 * Hook للاتصال بـ WebSocket للرسائل الفورية
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create WebSocket URL from backend URL
const getWsUrl = (token) => {
  const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
  const host = BACKEND_URL.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${host}/ws/messages/${token}`;
};

// Notification sound
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (e) {
    console.log('Could not play notification sound:', e);
  }
};

export const useWebSocket = (token, onNewMessage) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  const connect = useCallback(() => {
    if (!token) return;
    
    try {
      const wsUrl = getWsUrl(token);
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('📱 WebSocket connected');
        setIsConnected(true);
        
        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          
          if (data.type === 'new_message') {
            // Play notification sound
            if (data.play_sound) {
              playNotificationSound();
            }
            
            // Show toast notification
            const platform = data.platform === 'whatsapp' ? '💬 WhatsApp' : 
                           data.platform === 'facebook' ? '📘 Facebook' : 
                           data.platform === 'instagram' ? '📷 Instagram' : '💬';
            
            toast.info(
              `${platform}: ${data.customer_name}`,
              {
                description: data.message?.text?.substring(0, 50) + (data.message?.text?.length > 50 ? '...' : ''),
                duration: 5000,
                action: {
                  label: 'عرض',
                  onClick: () => {
                    // Navigate to conversations
                    window.location.href = '/conversations';
                  }
                }
              }
            );
            
            // Call the callback if provided
            if (onNewMessage) {
              onNewMessage(data);
            }
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('📴 WebSocket disconnected', event.code);
        setIsConnected(false);
        
        // Reconnect after 3 seconds if not intentional close
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('🔄 Reconnecting WebSocket...');
            connect();
          }, 3000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
    }
  }, [token, onNewMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000);
    }
  }, []);

  // Send ping to keep connection alive
  const ping = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  useEffect(() => {
    connect();
    
    // Ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(ping, 30000);
    
    return () => {
      clearInterval(pingInterval);
      disconnect();
    };
  }, [connect, disconnect, ping]);

  return { isConnected, lastMessage, reconnect: connect };
};

export default useWebSocket;
